import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageSEO from '@/components/feature/PageSEO';
import { itemListToProductGraph } from '@/lib/seo';
import ARangeHero from './components/ARangeHero';
import CoreCompetitiveness from './components/CoreCompetitiveness';
import SmartTechnology from './components/SmartTechnology';
import InteriorDisplay from './components/InteriorDisplay';
import MaterialsStructure from './components/MaterialsStructure';
import ApplicationScenarios from './components/ApplicationScenarios';
import TransportInstallation from './components/TransportInstallation';
import ProductModels from './components/ProductModels';
import Footer from '../home/components/Footer';

const aRangeJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'A Series Smart Modular Office Pods',
  description: 'Nexa Pods A Series — smart modular office pods for productivity. Apple-cabin-style workspaces from compact to executive models.',
  numberOfItems: 8,
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      item: {
        '@type': 'Thing',
        name: 'A6 Compact',
        description: 'Ideal compact workspace solution. Perfect for solo entrepreneurs and small businesses.',
        brand: { '@type': 'Brand', name: 'Nexa Pods' },
        url: 'https://nexapods.com.au/a-range-details',
        image: 'https://nexapods.com.au/images/e6379fec28de.webp',
      },
    },
    {
      '@type': 'ListItem',
      position: 2,
      item: {
        '@type': 'Thing',
        name: 'A9 Standard',
        description: 'Versatile workspace for small teams. Spacious meeting area with separate work zones.',
        brand: { '@type': 'Brand', name: 'Nexa Pods' },
        url: 'https://nexapods.com.au/a-range-details',
        image: 'https://nexapods.com.au/images/c28c289e3bc5.webp',
      },
    },
    {
      '@type': 'ListItem',
      position: 3,
      item: {
        '@type': 'Thing',
        name: 'A12 Executive',
        description: 'Premium workspace for executives. Includes conference room, kitchenette and restroom.',
        brand: { '@type': 'Brand', name: 'Nexa Pods' },
        url: 'https://nexapods.com.au/a-range-details',
        image: 'https://nexapods.com.au/images/8b41f7de70a0.webp',
      },
    },
    {
      '@type': 'ListItem',
      position: 4,
      item: {
        '@type': 'Product',
        name: 'A-S Elite',
        description: 'Elite compact workspace solution with premium materials and enhanced features.',
        brand: { '@type': 'Brand', name: 'Nexa Pods' },
        url: 'https://nexapods.com.au/a-range-details',
        image: 'https://nexapods.com.au/images/65a595c1c44c.webp',
        offers: {
          '@type': 'Offer',
          priceCurrency: 'AUD',
          price: '48620',
          availability: 'https://schema.org/InStock',
        },
      },
    },
    {
      '@type': 'ListItem',
      position: 5,
      item: {
        '@type': 'Product',
        name: 'A-P3 Elite',
        description: 'Premium elevated workspace with enhanced ceiling height for a more spacious feel.',
        brand: { '@type': 'Brand', name: 'Nexa Pods' },
        url: 'https://nexapods.com.au/a-range-details',
        image: 'https://nexapods.com.au/images/20363215da46.webp',
        offers: {
          '@type': 'Offer',
          priceCurrency: 'AUD',
          price: '51040',
          availability: 'https://schema.org/InStock',
        },
      },
    },
    {
      '@type': 'ListItem',
      position: 6,
      item: {
        '@type': 'Product',
        name: 'A-P5 Elite',
        description: 'Extended elite workspace with multiple functional zones. Perfect for growing teams.',
        brand: { '@type': 'Brand', name: 'Nexa Pods' },
        url: 'https://nexapods.com.au/a-range-details',
        image: 'https://nexapods.com.au/images/6a50655936e4.webp',
        offers: {
          '@type': 'Offer',
          priceCurrency: 'AUD',
          price: '53130',
          availability: 'https://schema.org/InStock',
        },
      },
    },
    {
      '@type': 'ListItem',
      position: 7,
      item: {
        '@type': 'Product',
        name: 'A-P7 Elite',
        description: 'Ultimate elite workspace solution with maximum capacity and premium features.',
        brand: { '@type': 'Brand', name: 'Nexa Pods' },
        url: 'https://nexapods.com.au/a-range-details',
        image: 'https://nexapods.com.au/images/f31cf4a3e2cc.webp',
        offers: {
          '@type': 'Offer',
          priceCurrency: 'AUD',
          price: '62150',
          availability: 'https://schema.org/InStock',
        },
      },
    },
    {
      '@type': 'ListItem',
      position: 8,
      item: {
        '@type': 'Product',
        name: 'A-P8 Double Layer',
        description: 'Innovative double-layer workspace maximising vertical space efficiency with two separate floors.',
        brand: { '@type': 'Brand', name: 'Nexa Pods' },
        url: 'https://nexapods.com.au/a-range-details',
        image: 'https://nexapods.com.au/images/5c4a47384fa7.webp',
        offers: {
          '@type': 'Offer',
          priceCurrency: 'AUD',
          price: '99000',
          availability: 'https://schema.org/InStock',
        },
      },
    },
  ],
};

const aRangeProductsJsonLd = itemListToProductGraph(aRangeJsonLd, {
  canonicalPath: '/a-range-details',
  requireNumericPrice: true,
});

export default function ARangeDetailsPage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [productsDropdownOpen, setProductsDropdownOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <PageSEO
        title="A Series Smart Modular Office Pods | Nexa Pods Australia"
        description="Explore Nexa A Series modular office pods in Melbourne, with compact to executive workspaces for home, commercial and project use across Australia."
        canonicalPath="/a-range-details"
        keywords="A Series office pods, modular workspace Australia, smart office pod Kew, prefab office VIC"
        jsonLd={aRangeProductsJsonLd}
      />
      <div className="bg-gradient-to-b from-[#0A0A0A] via-[#0D1117] to-[#000000] text-white min-h-screen w-full overflow-x-hidden">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 w-full ${
        scrolled ? 'bg-black/80 backdrop-blur-xl border-b border-cyan-500/20' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 flex items-center justify-between w-full">
          <Link to="/" className="flex items-center gap-3 flex-shrink-0">
            <img 
              src="/images/b58bd9e3723b.webp" 
              alt="Space Capsule Living" 
              className="h-16 sm:h-20 lg:h-28 w-auto"
            />
          </Link>
          
          {/* Mobile Menu Button */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-white cursor-pointer"
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-8">
            <Link to="/" className="text-base text-gray-300 hover:text-gray-200 transition-colors whitespace-nowrap cursor-pointer">Home</Link>
            
            {/* Products Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setProductsDropdownOpen(true)}
              onMouseLeave={() => setProductsDropdownOpen(false)}
            >
              <Link 
                to="/product-range"
                className="text-base text-gray-300 hover:text-gray-200 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
              >
                Products
                <i className={`ri-arrow-down-s-line text-lg transition-transform duration-300 ${productsDropdownOpen ? 'rotate-180' : ''}`}></i>
              </Link>
              
              {productsDropdownOpen && (
                <div className="absolute top-full left-0 pt-2 w-auto">
                  <div className="bg-black/90 backdrop-blur-xl border border-cyan-500/30 rounded-lg shadow-[0_0_20px_rgba(0,217,255,0.3)] overflow-hidden">
                    <Link 
                      to="/product-range" 
                      className="block px-6 py-3 text-base text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 hover:shadow-[inset_0_0_10px_rgba(0,217,255,0.2)] transition-all cursor-pointer whitespace-nowrap"
                    >
                      All Products
                    </Link>
                    <Link 
                      to="/a-range-details" 
                      className="block px-6 py-3 text-base text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 hover:shadow-[inset_0_0_10px_rgba(0,217,255,0.2)] transition-all cursor-pointer whitespace-nowrap"
                    >
                      A Series - Workplace Solution
                    </Link>
                    <Link 
                      to="/x-range-details" 
                      className="block px-6 py-3 text-base text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 hover:shadow-[inset_0_0_10px_rgba(0,217,255,0.2)] transition-all cursor-pointer whitespace-nowrap"
                    >
                      X Series - Luxury Living
                    </Link>
                    <Link 
                      to="/m-range-details" 
                      className="block px-6 py-3 text-base text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 hover:shadow-[inset_0_0_10px_rgba(0,217,255,0.2)] transition-all cursor-pointer whitespace-nowrap"
                    >
                      M Series - Modularize
                    </Link>
                    <Link 
                      to="/z-range-details" 
                      className="block px-6 py-3 text-base text-gray-400 hover:text-cyan-400 hover:bg-cyan-500/10 hover:shadow-[inset_0_0_10px_rgba(0,217,255,0.2)] transition-all cursor-pointer whitespace-nowrap"
                    >
                      Z Series - Coming Soon
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <a href="#models" className="text-base text-gray-300 hover:text-gray-200 transition-colors whitespace-nowrap cursor-pointer">Models</a>
            <a href="#applications" className="text-base text-gray-300 hover:text-gray-200 transition-colors whitespace-nowrap cursor-pointer">Applications</a>
            <Link to="/contact" className="px-6 py-2.5 bg-cyan-500 text-black text-base font-medium rounded-full hover:bg-cyan-400 transition-all hover:shadow-[0_0_20px_rgba(0,217,255,0.5)] whitespace-nowrap cursor-pointer">
              Contact Us
            </Link>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-cyan-500/20 w-full">
            <div className="px-4 py-6 space-y-4 w-full">
              <Link to="/" onClick={() => setMobileMenuOpen(false)} className="block text-base text-gray-300 hover:text-cyan-400 transition-colors py-2 cursor-pointer">Home</Link>
              
              {/* Mobile Products Dropdown */}
              <div>
                <button 
                  onClick={() => setProductsDropdownOpen(!productsDropdownOpen)}
                  className="w-full flex items-center justify-between text-base text-gray-300 hover:text-cyan-400 transition-colors py-2 cursor-pointer"
                >
                  Products
                  <i className={`ri-arrow-down-s-line text-lg transition-transform duration-300 ${productsDropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>
                
                {productsDropdownOpen && (
                  <div className="mt-2 ml-4 space-y-2 bg-black/90 border border-cyan-500/30 rounded-lg shadow-[0_0_15px_rgba(0,217,255,0.2)] overflow-hidden">
                    <Link 
                      to="/product-range" 
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 transition-all cursor-pointer whitespace-nowrap"
                    >
                      All Products
                    </Link>
                    <Link 
                      to="/a-range-details" 
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 transition-all cursor-pointer whitespace-nowrap"
                    >
                      A Series - Workplace Solution
                    </Link>
                    <Link 
                      to="/x-range-details" 
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 transition-all cursor-pointer whitespace-nowrap"
                    >
                      X Series - Luxury Living
                    </Link>
                    <Link 
                      to="/m-range-details" 
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 transition-all cursor-pointer whitespace-nowrap"
                    >
                      M Series - Modularize
                    </Link>
                    <Link 
                      to="/z-range-details" 
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-400 hover:text-cyan-400 hover:bg-cyan-500/10 transition-all cursor-pointer whitespace-nowrap"
                    >
                      Z Series - Coming Soon
                    </Link>
                  </div>
                )}
              </div>

              <a href="#models" onClick={() => setMobileMenuOpen(false)} className="block text-base text-gray-300 hover:text-cyan-400 transition-colors py-2 cursor-pointer">Models</a>
              <a href="#applications" onClick={() => setMobileMenuOpen(false)} className="block text-base text-gray-300 hover:text-cyan-400 transition-colors py-2 cursor-pointer">Applications</a>
              <Link to="/contact" className="block w-full text-center px-6 py-2.5 bg-cyan-500 text-black text-base font-medium rounded-full hover:bg-cyan-400 transition-all whitespace-nowrap cursor-pointer">
                Contact Us
              </Link>
            </div>
          </div>
        )}
      </nav>

      <ARangeHero />
      <ProductModels />
      <CoreCompetitiveness />
      <InteriorDisplay />
      <SmartTechnology />
      <MaterialsStructure />
      <ApplicationScenarios />
      <TransportInstallation />
      <Footer />
    </div>
    </>
  );
}
